import { useEffect, useMemo, useState } from 'react';

import { TRADE_LIST_PAGE_SIZE } from 'src/util/constants';
import { isNumber } from 'src/util/math';
import buildPaginationMetadata from 'src/util/pagination';

/**
 * Custom hook that encapsulates pagination logic for trade data.
 *
 * Handles:
 * - Current page state management
 * - Items per page configuration
 * - Pagination calculations (total pages, current page items)
 * - Page change handlers with validation
 * - Automatic pagination reset when data changes
 * @param {object} params - Hook parameters
 * @param {Array} params.items - Array of items to paginate
 * @param {number} [params.itemsPerPage=100] - Number of items to display per page
 * @param {object} params.timeRange - Time range object with start and finish
 * @param {object} params.filter - Filter object with direction and type
 * @returns {{handlePageChange: Function, paginationInfo: object}} Pagination state and handlers
 */
const useTradePagination = ({
  items = [],
  itemsPerPage = TRADE_LIST_PAGE_SIZE,
  timeRange,
  filter,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const paginationInfo = useMemo(
    () => buildPaginationMetadata(items, currentPage, itemsPerPage),
    [items, currentPage, itemsPerPage],
  );

  const handlePageChange = (page) => {
    if (!isNumber(page) || !paginationInfo
    || paginationInfo.pageCount === null || paginationInfo.pageCount === undefined) {
      return;
    }

    if (page < 1) {
      setCurrentPage(1);
    } else if (page > paginationInfo.pageCount) {
      setCurrentPage(paginationInfo.pageCount);
    } else {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [timeRange, filter]);

  useEffect(() => {
    if (paginationInfo?.pageCount > 0 && currentPage > paginationInfo?.pageCount) {
      setCurrentPage(1);
    }
  }, [paginationInfo?.pageCount, currentPage]);

  return {
    handlePageChange,
    paginationInfo,
  };
};

export default useTradePagination;
